export type Localised = {
  en: string
  ua: string
  ru: string
  ro: string
}

export type Appliance = {
  id: string
  name: Localised
  description: Localised
  /** Replaceable image field — swap the URL when clinical photos are ready. */
  image: string
}

export const appliances: Appliance[] = [
  {
    id: 'nance',
    name: {
      en: 'Nance Appliance',
      ua: 'Апарат Нансе',
      ru: 'Аппарат Нансе',
      ro: 'Aparat Nance',
    },
    description: {
      en: 'Stabilises the molars and maintains space during orthodontic treatment. Often used after distalisation or when space control is important.',
      ua: 'Стабілізує моляри та утримує простір під час ортодонтичного лікування. Часто застосовується після дистилізації або коли важливий контроль простору.',
      ru: 'Стабилизирует моляры и удерживает пространство во время ортодонтического лечения. Часто применяется после дистализации или когда важен контроль пространства.',
      ro: 'Stabilizează molarii și menține spațiul în timpul tratamentului ortodontic. Folosit adesea după distalizare sau când controlul spațiului este important.',
    },
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/0acd27ac-e54b-458b-b16a-703a67d4d3f7.jpg',
  },
  {
    id: 'haas-expander',
    name: {
      en: 'Haas Expander',
      ua: 'Експандер Хааса',
      ru: 'Экспандер Хааса',
      ro: 'Expandor Haas',
    },
    description: {
      en: 'A fixed appliance that widens the upper jaw, creating space for erupting teeth and reducing crowding — especially effective in growing children.',
      ua: 'Незнімний апарат для розширення верхньої щелепи. Створює простір для зубів, що прорізуються, і зменшує скупченість — особливо ефективний у дітей у період росту.',
      ru: 'Несъёмный аппарат для расширения верхней челюсти. Создаёт пространство для прорезывающихся зубов и уменьшает скученность — особенно эффективен у растущих детей.',
      ro: 'Un aparat fix care lărgește maxilarul superior, creând spațiu pentru dinții în erupție și reducând înghesuirea — deosebit de eficient la copiii în creștere.',
    },
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/146f2427-500c-424c-8f44-93ebe8bec47c.jpg',
  },
  {
    id: 'bioblock',
    name: {
      en: 'Bioblock Appliance',
      ua: 'Апарат Bioblock',
      ru: 'Аппарат Bioblock',
      ro: 'Aparat Bioblock',
    },
    description: {
      en: 'Supports healthy facial growth, jaw development and breathing patterns, guiding development in a more favourable direction.',
      ua: 'Підтримує гармонійний ріст обличчя, розвиток щелеп і правильне дихання, спрямовуючи розвиток у більш сприятливому напрямку.',
      ru: 'Поддерживает гармоничный рост лица, развитие челюстей и правильное дыхание, направляя развитие в более благоприятную сторону.',
      ro: 'Susține creșterea facială sănătoasă, dezvoltarea maxilarelor și respirația corectă, ghidând dezvoltarea într-o direcție mai favorabilă.',
    },
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/1a409a80-991d-4a69-8129-3e66096a3aa0.jpg',
  },
  {
    id: 'twin-block',
    name: {
      en: 'Twin Block',
      ua: 'Twin Block',
      ru: 'Twin Block',
      ro: 'Twin Block',
    },
    description: {
      en: 'A functional appliance used to correct a distal bite. It guides the lower jaw forward and supports growth correction in children and teenagers.',
      ua: 'Функціональний апарат для корекції дистального прикусу. Спрямовує нижню щелепу вперед і підтримує корекцію росту у дітей та підлітків.',
      ru: 'Функциональный аппарат для коррекции дистального прикуса. Направляет нижнюю челюсть вперёд и поддерживает коррекцию роста у детей и подростков.',
      ro: 'Un aparat funcțional folosit pentru corectarea ocluziei distale. Ghidează mandibula înainte și susține corectarea creșterii la copii și adolescenți.',
    },
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/cbccc9cc-9c30-423b-9532-839aafc3eff6.jpg',
  },
  {
    id: 'herbst',
    name: {
      en: 'Herbst Appliance',
      ua: 'Апарат Гербста',
      ru: 'Аппарат Гербста',
      ro: 'Aparat Herbst',
    },
    description: {
      en: 'A fixed functional appliance that positions the lower jaw forward, often used to correct bite problems related to lower jaw retrusion.',
      ua: 'Незнімний функціональний апарат, що виводить нижню щелепу вперед. Часто застосовується для корекції прикусу при ретрузії нижньої щелепи.',
      ru: 'Несъёмный функциональный аппарат, выдвигающий нижнюю челюсть вперёд. Часто применяется для коррекции прикуса при ретрузии нижней челюсти.',
      ro: 'Un aparat funcțional fix care poziționează mandibula înainte, folosit adesea pentru corectarea problemelor de ocluzie legate de retruzia mandibulei.',
    },
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/246a0b31-616e-46b3-bcbd-28ddabbe9769.jpg',
  },
  {
    id: 'distal-jet',
    name: {
      en: 'Distal Jet',
      ua: 'Distal Jet',
      ru: 'Distal Jet',
      ro: 'Distal Jet',
    },
    description: {
      en: 'A fixed appliance that moves the upper molars backwards, creating space and correcting certain bite relationships without relying on patient compliance.',
      ua: 'Незнімний апарат, який переміщує верхні моляри назад, створюючи простір і коригуючи прикус без потреби в постійній співпраці пацієнта.',
      ru: 'Несъёмный аппарат, перемещающий верхние моляры назад, создавая пространство и корректируя прикус без необходимости постоянного сотрудничества пациента.',
      ro: 'Un aparat fix care deplasează molarii superiori posterior, creând spațiu și corectând anumite relații ocluzale fără a depinde de cooperarea pacientului.',
    },
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/6329a84a-db83-424f-9f3e-82594c4829a8.jpg',
  },
  {
    id: 'marpe',
    name: {
      en: 'MARPE',
      ua: 'MARPE',
      ru: 'MARPE',
      ro: 'MARPE',
    },
    description: {
      en: 'Micro-implant Assisted Rapid Palatal Expansion — a fixed skeletal expander that widens the upper jaw with bone support. Useful for older teenagers and adults when stronger expansion is required.',
      ua: 'Швидке піднебінне розширення з опорою на мікроімпланти — незнімний скелетний експандер із кістковою опорою. Підходить старшим підліткам і дорослим, коли потрібне сильніше розширення.',
      ru: 'Быстрое нёбное расширение с опорой на микроимпланты — несъёмный скелетный экспандер с костной опорой. Подходит старшим подросткам и взрослым, когда требуется более сильное расширение.',
      ro: 'Expansiune palatinală rapidă asistată de microimplanturi — un expandor scheletal fix, cu sprijin osos. Util pentru adolescenți mai mari și adulți, când este necesară o expansiune mai puternică.',
    },
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/4a1c1ba8-2f64-4171-9ab5-8fc3b9aae6d3.jpg',
  },
  {
    id: 'space-maintainer',
    name: {
      en: 'Space Maintainer',
      ua: 'Утримувач простору',
      ru: 'Удерживатель пространства',
      ro: 'Menținător de spațiu',
    },
    description: {
      en: 'Used after early loss of baby teeth to preserve the space needed for permanent teeth, reducing the risk of future crowding and bite problems.',
      ua: 'Застосовується після ранньої втрати молочних зубів, щоб зберегти місце для постійних зубів і зменшити ризик скупченості та проблем із прикусом у майбутньому.',
      ru: 'Применяется после ранней потери молочных зубов, чтобы сохранить место для постоянных зубов и снизить риск скученности и проблем с прикусом в будущем.',
      ro: 'Folosit după pierderea timpurie a dinților de lapte pentru a păstra spațiul necesar dinților permanenți, reducând riscul de înghesuire și probleme de ocluzie.',
    },
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/1b516513-1bca-4a8e-b1c4-4374066858e9.jpg',
  },
]
